/**
 * BOOKINGPAGE.JS | Micheals Infrastructure Advisory
 * COMPONENT: Consultation Engagement Interface
 * VERSION: 2026.CORE.V3.FIDUCIARY
 * REVISION: 3.3.1 - Enhanced behavioral tracking visualization.
 */

import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Binary, Activity, ChevronRight } from 'lucide-react';
import InteractiveBooking from "../components/InteractiveBooking"; 
import { submitBooking } from "../components/services/avaAPI";

const BookingPage = ({ sessionId }) => {
  const savedName = sessionStorage.getItem('leadName') || '';
  const savedEmail = sessionStorage.getItem('leadEmail') || '';
  const savedPhone = sessionStorage.getItem('leadPhone') || '';
  const rawHistory = sessionStorage.getItem('history');
  const history = rawHistory ? JSON.parse(rawHistory) : [];

  const getHotTopic = (arr) => {
    if (!arr || !arr.length) return 'General Infrastructure Architecture';
    const frequency = {};
    arr.forEach(path => { 
      let clean = path.replace(/\//g, '').toUpperCase();
      if (clean.includes('LIGHT')) clean = 'Luminous Environment Design';
      if (clean.includes('GALLERY')) clean = 'System Aesthetics & Integration';
      if (clean.includes('DESIGN')) clean = 'Infrastructure Schematics';
      
      const category = clean || 'CORE_DESIGN_LOGIC';
      frequency[category] = (frequency[category] || 0) + 1; 
    });
    return Object.keys(frequency).reduce((a, b) => frequency[a] > frequency[b] ? a : b);
  };

  const hotTopic = getHotTopic(history);

  const transmitConsultationBrief = async (bookingDetails) => {
    const analysisData = {
        sessionId: sessionId || sessionStorage.getItem('avaSessionId'),
        leadName: savedName,
        leadEmail: savedEmail,
        leadPhone: savedPhone,
        appointment: bookingDetails, 
        primaryConcentration: hotTopic,
        browsingLogic: history, 
        timestamp: new Date().toISOString(),
        firmModel: "Principal_Consultation",
        status: "PRE_AUDIT_STAGING"
    };

    try {
      await submitBooking(analysisData);
      console.log("🧠 Principal Archive: Technical Briefing Staged.");
    } catch (err) {
      console.debug("Analytics deferred. Manual synchronization required.");
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f8f7] pt-32 pb-20 px-6 selection:bg-stone-200">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
          
          {/* LEFT COLUMN: BEHAVIORAL INTELLIGENCE */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1 space-y-12"
          >
            <div className="space-y-4">
                <span className="text-[10px] font-black text-amber-700 uppercase tracking-[0.6em] flex items-center gap-2">
                  <Activity size={12} className="animate-pulse" /> Engagment Phase // Final Intake
                </span>
                <h1 className="text-5xl font-serif italic text-stone-950 leading-[1.1]">
                  Initiate <br />
                  <span className="not-italic font-light text-stone-400">Technical Audit.</span>
                </h1>
            </div>
            
            <div className="p-10 bg-white border border-stone-200 rounded-sm shadow-sm">
                <p className="text-[10px] uppercase tracking-widest font-black text-amber-700 mb-4 flex items-center gap-2">
                    <Binary size={12} /> Detected Concentration
                </p>
                <p className="text-stone-950 font-serif italic text-lg leading-relaxed mb-6">
                  {hotTopic}
                </p>
                <div className="text-stone-500 font-light text-xs leading-relaxed border-t border-stone-100 pt-6">
                  Our diagnostic engine has mapped your primary interest. Our auditor will review these parameters to ensure the consultation is centered on your specific infrastructure objectives.
                </div>
            </div>
            
            <div className="flex items-center gap-4 text-stone-500 border-l-2 border-stone-300 pl-6">
              <ShieldCheck size={20} className="text-amber-700" />
              <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest font-black text-stone-950">Independent Oversight</span>
                  <span className="text-[10px] text-stone-500 font-light italic">Conflict-free technical advocacy</span>
              </div>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: GATEWAY */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2 bg-white rounded-sm shadow-lg border border-stone-200 p-12 flex flex-col items-center justify-center min-h-[650px] relative"
          >
            <div className="w-full relative z-10">
              <InteractiveBooking 
                sessionId={sessionId}
                leadName={savedName}
                leadEmail={savedEmail}
                initialService={hotTopic}
                onBookingComplete={(bookingData) => transmitConsultationBrief(bookingData)} 
              />
            </div>
          </motion.div>
        </div>

        <footer className="flex justify-between border-t border-stone-200 pt-12 items-center">
            <p className="text-[9px] uppercase tracking-[0.5em] text-stone-400 font-black">
                Micheals Infrastructure Advisory • Secure Gateway • 2026
            </p>
            <div className="flex items-center gap-2 text-stone-400">
                <ChevronRight size={12} />
                <span className="text-[9px] uppercase tracking-[0.3em] font-black">Proceed to Fiduciary Review</span>
            </div>
        </footer>
      </div>
    </div>
  );
};

export default BookingPage;